import type { ReleaseCitySubdivisionSource } from '@outkeep/shared';
import { pure } from '@core/helpers/pure';
import type { Nullable } from '@core/helpers';
import { Languages, RequestStatuses } from '@core/types';
import {
  LOGISTIC_REQUEST_EXTRA_FIELDS,
  LogisticRequestExtraField,
  LogisticRequestType,
} from '@outkeep/shared';
import type {
  CarBrand,
  Counterparty,
  CounterpartySubdivision,
  InstallZone,
  LogisticRequestRatingList,
  Person,
  RegistryCounterparty,
  RequestDevice,
  RequestDTO,
  RequestObdStatus,
  RequestProduct,
  RequestStatus,
  User,
  LogisticProductPresetSnapshot,
} from '@core/types';

export class RequestModel implements RequestDTO {
  id!: number;
  requestNumber!: string;
  requestType!: LogisticRequestType;
  extra!: Record<string, unknown>;
  projectNumber!: string;
  contract?: Nullable<string> | undefined;
  contractDate?: Nullable<string> | undefined;
  contractPlannedEndDate?: Nullable<string> | undefined;
  contractEarlyEndDate?: Nullable<string> | undefined;
  lesseeSigning!: Record<string, unknown>;
  statusId!: Nullable<RequestStatus>;
  counterpartyOwnerId!: RegistryCounterparty;
  releaseDate?: string | undefined;
  actualReleaseDate?: string | undefined;
  completedAt?: string | undefined;
  releaseTime?: string | undefined;
  products?: RequestProduct[] | undefined;
  actualDeliveryAddress?: string | undefined;
  shipmentDocsAddress!: string;
  devices!: RequestDevice[];
  carModelAndBrand!: Nullable<string>;
  carBrandName!: Nullable<string>;
  carBrandId!: Nullable<CarBrand>;
  carModel!: string;
  shortCarModel?: string | undefined;
  carType!: string;
  carVin!: string;
  carPlantNumber!: string;
  carYear!: string;
  manufacturerCountry!: string;
  subjName!: string;
  subjInn!: string;
  subjFullname!: string;
  subjPhone!: string;
  ownerLogistFullname!: string;
  ownerLogistPhone!: string;
  leasingProduct!: string;
  extendedProgram!: string;
  contractorCounterpartyId!: Nullable<Counterparty>;
  contractorCounterpartySubdivisionId!: Nullable<CounterpartySubdivision>;
  installZoneId!: Nullable<InstallZone>;
  installZoneNameCashed!: string;
  installPrice!: string;
  installerId!: Nullable<Person>;
  createdId!: User;
  createRequestParentId!: Nullable<User>;
  obdStatus!: Nullable<RequestObdStatus>;
  obdReadSuccess!: Nullable<boolean>;
  obdComment!: string;
  supportManagerFullname!: string;
  supportManagerPhone!: string;
  clientManagerFullname!: string;
  clientManagerPhone!: string;
  clientManagerEmail!: string;
  lesseeSigningAddress!: string;
  lesseeSigningInstallerId!: string;
  lesseeSigningDate!: string;
  lesseeSigningContractorSubdivisionId!: string;
  lesseeSigningStatusId!: string;
  deliveryName?: string | undefined;
  deliveryAddress?: string | undefined;
  deliveryPersonName?: string | undefined;
  deliveryPersonPhone?: string | undefined;
  deliveryPersonEmail?: string | undefined;
  createdViaApi?: boolean | undefined;
  templateType?: string | undefined;
  pts?: string | undefined;
  useOwnerAct?: boolean | undefined;
  qualityControlStatus?: number | undefined;
  timezone?: string | undefined;
  ratings!: Nullable<LogisticRequestRatingList[]>;
  ratingsWithDetails!: Nullable<LogisticRequestRatingList[]>;
  cancelReason?: string;
  subjInitials?: string | undefined; // Инициалы лизингополучателя
  carCategory?: string | undefined; // Категория ТС
  carEngineNumber?: string | undefined; // Номер двигателя
  hasEDO?: boolean | undefined; // Электронный оборот для АЛЬФА ЛИЗИНГ
  inspectionResult?: Nullable<boolean>; // Результат проверки предмета лизинга
  productPresetSnapshot?: Nullable<LogisticProductPresetSnapshot>;
  createdAt!: string;
  updatedAt?: string;
  releaseCitySubdivisionId?: number;
  releaseCitySubdivisionSource?: ReleaseCitySubdivisionSource | null;

  constructor(request: RequestDTO) {
    Object.assign(this, request);
    this.products = (request.products || []).sort((a, b) => {
      return a.requestProductId?.name.localeCompare(b.requestProductId?.name, [
        Languages.ENGLISH,
        Languages.RUSSIAN,
      ]);
    });
  }

  set releaseDateTime(value) {
    // TODO: с бэкенда приходит это поле, но пустое. нужно ли оно там?
  }

  @pure
  get releaseDateTime(): string {
    // TODO: формат 9 августа, 20:00 (UTC+3)
    return [this.releaseDate, this.releaseTime].filter(Boolean).join(' ');
  }

  @pure
  get car(): string {
    return (
      this.shortCarModel ||
      this.carModelAndBrand ||
      [this.carBrandName || this.carBrandId?.name, this.carModel]
        .filter(Boolean)
        .join(' ')
    );
  }

  @pure
  get vin(): string {
    return this.carVin || this.carPlantNumber || '';
  }

  @pure
  get installerShort(): string {
    if (!this.installerId) {
      // return 'Не назначен';
      return ''; // TODO проверить что не ломает логику если ломает, то добавить переводы на 'Не назначен'
    }

    const { firstName, lastName, patronymic } = this.installerId;

    return `${lastName} ${firstName?.charAt(0)}. ${patronymic?.charAt(0)}.`;
  }

  @pure
  get idOfInstaller(): Nullable<number> {
    return this.installerId?.id ?? null;
  }

  @pure
  get subdivisionName(): string {
    return this.contractorCounterpartySubdivisionId?.name || '';
  }

  @pure
  get counterPartyName(): string {
    return this.counterpartyOwnerId.shortName || this.counterpartyOwnerId.name;
  }

  @pure
  get isFavorite(): boolean {
    // TODO ЛОГИКА ДЛЯ ПРОВЕРКИ НА ИЗБРАННОЕ
    return false;
  }

  @pure
  get isReleaseDateTimeFilledInRequest(): boolean {
    return !!this.releaseDate && !!this.releaseTime;
  }

  @pure
  get isReleaseDateTimeNotInFuture(): boolean {
    const { releaseDate } = this;

    if (!releaseDate) throw new Error('releaseDate должна быть указана');

    const releaseDateOnlyDay = new Date(releaseDate);
    const todayDay = new Date();

    releaseDateOnlyDay.setHours(0, 0, 0, 0);
    todayDay.setHours(0, 0, 0, 0);

    return releaseDateOnlyDay <= todayDay;
  }

  @pure
  get needToCheckRequiredFields(): boolean {
    const { releaseDate } = this;
    const minDate = new Date('2019-11-11');

    return (releaseDate && new Date(releaseDate) > minDate) || true;
  }

  @pure
  get areAllProductsCompleted(): boolean {
    const { products } = this;

    if (!products?.length) {
      return true;
    }

    return products.every(product => product.completed);
  }

  @pure
  get isCompleted(): boolean {
    return this.statusId?.id === RequestStatuses.Completed;
  }

  @pure
  get actualAddressConfirmed(): boolean {
    return !!this.actualDeliveryAddress;
  }

  @pure
  get visibleExtraFields(): LogisticRequestExtraField[] {
    return LOGISTIC_REQUEST_EXTRA_FIELDS.filter(field => {
      const value = this.extra?.[field.name];

      return (
        (!field.requestType || field.requestType === this.requestType) &&
        value !== null &&
        value !== undefined &&
        value !== ''
      );
    });
  }

  getExtraFieldValue(field: LogisticRequestExtraField): string {
    const value = this.extra?.[field.name];

    return typeof value === 'string' || typeof value === 'number'
      ? String(value)
      : '';
  }
}
